import { useCallback, useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import {
  EXISTING_ENTRY_HIGH_CONFIDENCE,
  type ExistingEntryMatch,
} from '@accounting/shared';
import { formatAccountingDate, formatCurrency } from '../../lib/formatters';

/**
 * "You already posted this" — shown inline on an unmatched bank feed row when a
 * journal entry the owner keyed by hand looks like the same transaction.
 *
 * Without this the feed only ever offers to CREATE an entry, so the hand-posted
 * one silently becomes a duplicate. Match-to-existing lives in the drawer, but
 * only a user who suspects the duplicate goes looking for it.
 */

const DISMISS_STORAGE_KEY = 'bank.existingEntrySuggestions.dismissed';

/**
 * Dismissals are kept in localStorage rather than the database: "not a match"
 * is a per-person UI preference, not an accounting fact, and recording it would
 * need a table + migration for something a browser can hold. Keyed by
 * transaction + entry so dismissing one suggestion reveals the next candidate
 * rather than silencing the row entirely.
 */
function readDismissed(): Record<string, true> {
  try {
    const raw = localStorage.getItem(DISMISS_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Record<string, true>) : {};
  } catch {
    return {};
  }
}

function writeDismissed(next: Record<string, true>): void {
  try {
    localStorage.setItem(DISMISS_STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Private mode / quota — dismissal just won't survive a reload.
  }
}

const dismissKey = (transactionId: string, journalEntryId: string) =>
  `${transactionId}:${journalEntryId}`;

/**
 * Filters the server's suggestions down to the ones this user hasn't waved off.
 * Returns them alongside the dismiss action so the row can decide whether it has
 * anything to render before it renders anything.
 */
export function useVisibleExistingEntryMatches(
  transactionId: string,
  matches: ExistingEntryMatch[],
): { visible: ExistingEntryMatch[]; dismiss: (journalEntryId: string) => void } {
  const [dismissed, setDismissed] = useState<Record<string, true>>(readDismissed);

  const dismiss = useCallback(
    (journalEntryId: string) => {
      setDismissed((prev) => {
        const next = { ...prev, [dismissKey(transactionId, journalEntryId)]: true as const };
        writeDismissed(next);
        return next;
      });
    },
    [transactionId],
  );

  const visible = useMemo(
    () => matches.filter((m) => !dismissed[dismissKey(transactionId, m.journalEntryId)]),
    [matches, dismissed, transactionId],
  );

  return { visible, dismiss };
}

export function ExistingEntrySuggestionCard({
  match,
  otherCount,
  isOut,
  isLinking,
  onLink,
  onDismiss,
  onReview,
}: {
  match: ExistingEntryMatch;
  /** How many further candidates sit behind this one. */
  otherCount: number;
  /** Money left the account (drives the DR/CR wording). */
  isOut: boolean;
  isLinking: boolean;
  onLink: () => void;
  onDismiss: () => void;
  onReview: () => void;
}) {
  const pct = Math.round(match.confidence * 100);
  const isHighConfidence = match.confidence >= EXISTING_ENTRY_HIGH_CONFIDENCE;

  return (
    <div
      className="card"
      style={{
        marginTop: 6,
        padding: '6px 10px',
        fontSize: 12,
        background: 'var(--bg-surface)',
        borderLeft: '3px solid var(--accent)',
        display: 'inline-block',
        maxWidth: '100%',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <span className={isHighConfidence ? 'tag ok' : 'tag'} title={match.reason}>
          {isHighConfidence ? 'Already posted?' : 'Possible duplicate'} · {pct}%
        </span>
        <span>
          <Link to="/journal/$id" params={{ id: match.journalEntryId }}>
            {match.entryNumber}
          </Link>
          {' · '}
          <strong>{formatAccountingDate(match.date)}</strong>
          {' · '}
          <strong>
            {formatCurrency(Math.abs(parseFloat(match.signedMovement)).toFixed(2))}
          </strong>
          {' '}
          <span className="muted">{isOut ? '(CR bank)' : '(DR bank)'}</span>
        </span>
      </div>

      <div className="muted" style={{ marginTop: 2 }}>
        {match.reason}
      </div>
      {match.memo && (
        <div style={{ marginTop: 2, fontStyle: 'italic' }}>"{match.memo}"</div>
      )}

      <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
        <button
          type="button"
          className="btn-primary"
          onClick={onLink}
          disabled={isLinking}
          title={`Link this transaction to ${match.entryNumber}. No new entry is created.`}
        >
          {isLinking ? 'Linking…' : `Link to ${match.entryNumber}`}
        </button>
        <button
          type="button"
          className="btn-secondary"
          onClick={onDismiss}
          disabled={isLinking}
          title={
            otherCount > 0
              ? 'Hide this suggestion and show the next candidate.'
              : 'Hide this suggestion for this transaction.'
          }
        >
          Not a match
        </button>
        {otherCount > 0 && (
          <button
            type="button"
            className="btn-secondary"
            onClick={onReview}
            title={`${otherCount} other entr${otherCount === 1 ? 'y' : 'ies'} could also be this transaction`}
          >
            {otherCount} other{otherCount === 1 ? '' : 's'}…
          </button>
        )}
      </div>
    </div>
  );
}
